import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Col, Form, message, Row } from 'antd';
import { Routes } from 'config/routes';
import {
  CreateContactInput,
  InviteUserRole,
  useInviteUserMutation,
} from 'gql/types';
import { Button, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AdditionalContactsSection from './AdditionalContactsSection';
import PersonalInfoSection from './PersonalInfoSection';
import { createProfileFormSchema, CreateProfileFormSchemaType } from './schema';
import styles from './styles.module.scss';

import { contactSchema } from '../EditProfileForm/schema';

const CreateProfileForm: FC = () => {
  const { t } = useTranslation('owner.CreateOwnerPage');
  const navigate = useNavigate();

  const createOwnerForm = useForm<CreateProfileFormSchemaType>({
    resolver: yupResolver(createProfileFormSchema),
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    getValues,
  } = createOwnerForm;

  const [inviteUser, inviteUserMutation] = useInviteUserMutation({
    onCompleted: () => {
      reset();
      message.success(t('successfullyInvited'));
    },
    onError: ({ message: m }) => {
      message.error(m);
    },
  });

  const contactFormIsValid = () => {
    const contactsFieldNames = Object.keys(
      contactSchema.fields
    ) as (keyof CreateProfileFormSchemaType['contact'])[];
    const someFieldIsInvalid = contactsFieldNames.some((fieldName) => {
      return !createOwnerForm.formState.dirtyFields.contact?.[fieldName];
    });
    return (
      !createOwnerForm.getFieldState('contact').error && !someFieldIsInvalid
    );
  };

  const submitForm = async (formData: CreateProfileFormSchemaType) => {
    const { personalInfo, contact } = formData;

    inviteUser({
      variables: {
        input: {
          ...personalInfo,
          role: InviteUserRole.Owner,
          additionalContact: contactFormIsValid()
            ? (contact as CreateContactInput)
            : undefined,
        },
      },
    });
  };

  const inviteHandler = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = getValues();
    await submitForm(formData);
  };

  const handleClose = () => {
    reset();
    navigate(Routes.Owners);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  return (
    <>
      <Spin spinning={inviteUserMutation.loading}>
        <FormProvider {...createOwnerForm}>
          <Form labelCol={{ span: 12 }} layout="vertical">
            <Row gutter={[10, 0]}>
              <Col sm={12} span={24}>
                <PersonalInfoSection fieldNamePrefix="personalInfo" />
              </Col>
              <Col sm={12} span={24}>
                <AdditionalContactsSection fieldNamePrefix="contact" />
              </Col>
            </Row>
          </Form>
        </FormProvider>
        <Row className={styles.bottomFormButtonsContainer}>
          <Col span={24}>
            <Row justify="space-between">
              <Col>
                <Button onClick={handleClose}>{t('close')}</Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  disabled={!createOwnerForm.formState.isValid}
                  onClick={inviteHandler}
                >
                  {t('create')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={inviteUserMutation.loading}
      />
    </>
  );
};

export default CreateProfileForm;
